import {
    Avatar,
    Box,
    chakra,
    createSlotRecipeContext,
    defineStyle,
    Flex,
    type FlexProps,
    HStack,
    Icon,
    IconButton,
    Popover,
    Portal,
    Separator,
    Text,
    VisuallyHidden,
    VStack,
} from '@chakra-ui/react';
import type React from 'react';
import { useState } from 'react';
import { LuExternalLink } from 'react-icons/lu';
import { RiLogoutBoxLine } from 'react-icons/ri';
import { type LinkProps, NavLink } from 'react-router';
import { myAccountMenuRecipe } from './my-account-menu.recipe';
import { chromeControlStyles, chromeSegmentStyles } from './sidebar';

const { withProvider, withContext } = createSlotRecipeContext({
    recipe: myAccountMenuRecipe,
});

const MyAccountMenuPopoverRootEl = withProvider<
    typeof Popover.Root,
    Popover.RootProps
>(Popover.Root, 'root');
const MyAccountAvatarEl = withContext<typeof Avatar.Root, Avatar.RootProps>(
    Avatar.Root,
    'trigger',
);
const MyAccountPopoverContentHeaderEl = withContext<
    typeof Popover.Header,
    Popover.HeaderProps
>(Popover.Header, 'header');
const MyAccountPopoverContentBodyEl = withContext<
    typeof Popover.Body,
    Popover.BodyProps
>(Popover.Body, 'body');
const MyAccountMenuItemEl = withContext<typeof Flex, FlexProps>(Flex, 'item');

const ringCss = defineStyle({
    outlineWidth: '1px',
    outlineColor: 'border',
    outlineOffset: '2px',
    outlineStyle: 'solid',
});

// App-agnostic account menu: identity and the logout URL come as props, and
// the menu items are composed by the app via the Item* subcomponents (they
// carry app business — routes, external policy links, tour actions…). The
// function-children form receives `dismiss` so item clicks can close the
// popover.
export type MyAccountMenuProps = {
    /** Display name shown in the identity header and used for avatar initials. */
    name: string;
    /** The trigger's label beside the chip (e.g. the first name); the full
     * `name` when absent. */
    triggerLabel?: string;
    email: string;
    avatarUrl?: string;
    logoutHref: string;
    /** Optional click-side effect for logout (e.g. a same-tab replace). */
    onLogout?: (event: React.MouseEvent) => void;
    /** Optional muted build stamp rendered under the email. */
    /** Identity header background image (app-owned asset). */
    headerBackgroundUrl?: string;
    buildInfo?: React.ReactNode;
    children?: React.ReactNode | ((dismiss: () => void) => React.ReactNode);
};

function MyAccountMenuRoot({
    name,
    triggerLabel,
    email,
    avatarUrl,
    logoutHref,
    onLogout,
    headerBackgroundUrl,
    buildInfo,
    children,
}: MyAccountMenuProps) {
    const [open, setOpen] = useState(false);
    const dismiss = () => setOpen(false);

    return (
        <MyAccountMenuPopoverRootEl
            open={open}
            autoFocus={false}
            onOpenChange={(e) => setOpen(e.open)}
            positioning={{ placement: 'bottom-end' }}
        >
            <Popover.Trigger asChild>
                {
                    /* A real <button>, not a Box: a div trigger is
                    unreachable by keyboard — no tab stop, no focus ring, no
                    Enter/Space activation. The ring lives here (the
                    focusable element), on the same token every other
                    control resolves. A flat cell of the top bar
                    (chromeSegmentStyles) — [avatar] [name] — flush with the
                    bar's end, the 24px chip and the name reading as one
                    label. */
                    <chakra.button
                        type='button'
                        aria-expanded={open}
                        cursor='pointer'
                        userSelect='none'
                        display='flex'
                        alignItems='center'
                        gap={2}
                        ps={4}
                        pe={4}
                        // Flush with the bar's end (its 16px inset
                        // cancelled).
                        me={-4}
                        color='{colors.fg}'
                        {...chromeSegmentStyles}
                    >
                        <MyAccountAvatarEl shape='full' aria-hidden>
                            <Avatar.Fallback
                                data-sentry-mask
                                name={name}
                                fontSize='2xs'
                            />
                            {/* Decorative: the trigger button already
                                carries the "My account" label. */}
                            <Avatar.Image src={avatarUrl} alt='' />
                        </MyAccountAvatarEl>
                        <Text
                            data-sentry-mask
                            fontSize='sm'
                            color='{colors.fg}'
                            lineHeight={1.2}
                            maxW='40'
                            truncate
                        >
                            {triggerLabel ?? name}
                        </Text>
                        {/* Visible name first, purpose after ("Ada, my account") —
                            voice control matches what is on screen. */}
                        <VisuallyHidden>, my account</VisuallyHidden>
                    </chakra.button>
                }
            </Popover.Trigger>
            <Portal>
                <Popover.Positioner>
                    {/* Width via --popover-size (same trick as the
                        notifications menu): a plain `w` would override the
                        theme's small-screen min() guard. */}
                    <Popover.Content css={{ '--popover-size': 'sizes.2xs' }}>
                        {/* Identity header + menu items share one scroll region clamped by
                            the positioner's --available-height; Log Out stays
                            pinned and reachable on short viewports. */}
                        <MyAccountPopoverContentBodyEl
                            roundedTop='md'
                            minH={0}
                            overflowY='auto'
                        >
                            <MyAccountPopoverContentHeaderEl
                                css={
                                    headerBackgroundUrl
                                        ? {
                                              // Quoted (and quote-stripped) so
                                              // URLs with parens/spaces/query
                                              // strings can't invalidate the
                                              // declaration or inject styles.
                                              '--account-menu-header-bg': `url("${headerBackgroundUrl.replaceAll('"', '')}")`,
                                          }
                                        : undefined
                                }
                            >
                                {/* User info, compact: avatar left, identity
                                    stacked beside it. */}
                                <HStack gap={3} px={4} py={4} align='center'>
                                    <Avatar.Root
                                        css={ringCss}
                                        // As tall as the identity block
                                        // beside it.
                                        boxSize={12}
                                        flexShrink={0}
                                    >
                                        <Avatar.Fallback
                                            data-sentry-mask
                                            name={name}
                                            fontSize='sm'
                                        />
                                        {/* Decorative: the name text sits
                                            beside it. */}
                                        <Avatar.Image src={avatarUrl} alt='' />
                                    </Avatar.Root>
                                    <VStack
                                        align='start'
                                        gap={0}
                                        minW={0}
                                        lineHeight={1.35}
                                        // On the always-dark cap (the tour
                                        // header's inks): the name white,
                                        // the email and build stamp light
                                        // gray at the email's size.
                                        css={{
                                            '& *': {
                                                fontSize: 'xs',
                                                color: '#C0C0C3',
                                            },
                                            '& > :first-child': {
                                                fontSize: 'sm',
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        <Text
                                            data-sentry-mask
                                            fontWeight='medium'
                                            maxW='100%'
                                            truncate
                                        >
                                            {name}
                                        </Text>
                                        <Text
                                            data-sentry-mask
                                            maxW='100%'
                                            truncate
                                        >
                                            {email}
                                        </Text>
                                        {buildInfo}
                                    </VStack>
                                </HStack>
                            </MyAccountPopoverContentHeaderEl>
                            <Separator borderColor='border' />
                            {/* Menu items (app-composed) */}
                            <VStack align='stretch' gap={0} py={1.5} px={2}>
                                {typeof children === 'function'
                                    ? children(dismiss)
                                    : children}
                            </VStack>
                        </MyAccountPopoverContentBodyEl>
                        {/* Logout */}
                        {/* No bg: the sign-out cap sits on the same tone as
                            the item list — only the identity cap above carries
                            its own fill. */}
                        <Box flexShrink={0} roundedBottom='md'>
                            <Separator borderColor='border' />
                            <AccountMenuItemExternalLink
                                href={logoutHref}
                                label='Log Out'
                                icon={RiLogoutBoxLine}
                                // Logging out must never fork a new tab;
                                // onLogout can still take over the whole
                                // navigation (e.g. a same-tab replace with
                                // extra params).
                                target='_self'
                                hideIndicator={true}
                                intent='danger'
                                onClick={onLogout}
                                px={4}
                                py={3}
                                rounded='none'
                                roundedBottom='md'
                            />
                        </Box>
                    </Popover.Content>
                </Popover.Positioner>
            </Portal>
        </MyAccountMenuPopoverRootEl>
    );
}

MyAccountMenuRoot.displayName = 'MyAccountMenu';

type BaseItemLinkProps = FlexProps & {
    label: string;
    icon: React.ElementType;
    onClick?: (event: React.MouseEvent) => void;
};

interface ItemExternalLinkProps extends BaseItemLinkProps {
    /** Middle-button activations emit auxclick, not click. */
    onAuxClick?: (event: React.MouseEvent) => void;
    href: string;
    /** Anchor target. Defaults to a new tab; flows that must stay in-tab
        (logout) pass '_self'. */
    target?: React.HTMLAttributeAnchorTarget;
    hideIndicator?: boolean;
    intent?: 'normal' | 'danger';
}

function AccountMenuItemExternalLink({
    href,
    label,
    icon: IconComponent,
    target = '_blank',
    hideIndicator = false,
    intent = 'normal',
    onClick,
    onAuxClick,
    ...props
}: ItemExternalLinkProps) {
    return (
        <a
            href={href}
            target={target}
            rel='noopener noreferrer'
            onClick={onClick}
            onAuxClick={onAuxClick}
            // outline:none only suppresses the UA ring on the raw anchor —
            // the item recipe redraws the themed ring on the row.
            style={{ textDecoration: 'none', outline: 'none' }}
        >
            <MyAccountMenuItemEl
                data-intent={intent}
                className='group'
                {...props}
            >
                {IconComponent && <Icon as={IconComponent} boxSize={4} />}
                <Text>{label}</Text>
                {!hideIndicator && (
                    <Icon
                        as={LuExternalLink}
                        // Same size as the sidebar's external indicator.
                        boxSize={{ base: 4, md: 3.5 }}
                        ml='auto'
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                        transition='opacity 0.2s ease'
                    />
                )}
            </MyAccountMenuItemEl>
        </a>
    );
}

interface ItemLinkProps extends BaseItemLinkProps {
    to: LinkProps['to'];
}

function AccountMenuItemLink({
    to,
    label,
    icon: IconComponent,
    onClick,
    ...props
}: ItemLinkProps) {
    return (
        <NavLink
            to={to}
            // Same deal: the item recipe draws the focus ring.
            style={{ textDecoration: 'none', outline: 'none' }}
            onClick={onClick}
        >
            <MyAccountMenuItemEl {...props}>
                {IconComponent && <Icon as={IconComponent} boxSize={4} />}
                <Text>{label}</Text>
            </MyAccountMenuItemEl>
        </NavLink>
    );
}

function AccountMenuItemButton({
    label,
    icon: IconComponent,
    onClick,
    ...props
}: BaseItemLinkProps) {
    return (
        <MyAccountMenuItemEl
            as='button'
            width='full'
            textAlign='left'
            onClick={onClick}
            {...props}
        >
            {IconComponent && <Icon as={IconComponent} boxSize={4} />}
            <Text>{label}</Text>
        </MyAccountMenuItemEl>
    );
}

type MyAccountMenuNamespace = typeof MyAccountMenuRoot & {
    ItemLink: typeof AccountMenuItemLink;
    ItemButton: typeof AccountMenuItemButton;
    ItemExternalLink: typeof AccountMenuItemExternalLink;
};

const MyAccountMenu = Object.assign(MyAccountMenuRoot, {
    ItemLink: AccountMenuItemLink,
    ItemButton: AccountMenuItemButton,
    ItemExternalLink: AccountMenuItemExternalLink,
}) as MyAccountMenuNamespace;

export type IconMenuProps = {
    /** Accessible name of the trigger button. */
    'aria-label': string;
    /** Trigger glyph (an <Icon>-wrapped component). */
    icon: React.ReactNode;
    /** Popover placement — footer triggers pass a top-* placement. */
    positioning?: Popover.RootProps['positioning'];
    children?: React.ReactNode | ((dismiss: () => void) => React.ReactNode);
};

/** Lean sibling of MyAccountMenu on the same recipe: an icon-button trigger
 * opening a plain sectioned menu (no hero card, no logout). Rows compose
 * from the shared MyAccountMenu.Item* primitives; section headings from
 * IconMenu.SectionTitle. */
function IconMenuRoot({
    'aria-label': ariaLabel,
    icon,
    positioning,
    children,
}: IconMenuProps) {
    const [open, setOpen] = useState(false);
    const dismiss = () => setOpen(false);

    return (
        <MyAccountMenuPopoverRootEl
            open={open}
            autoFocus={false}
            onOpenChange={(e) => setOpen(e.open)}
            positioning={{ placement: 'bottom-end', ...positioning }}
        >
            <Popover.Trigger asChild>
                {/* Same ghost treatment as the other chrome icon controls
                    (sidebar toggle, ColorModeButton). */}
                <IconButton
                    aria-label={ariaLabel}
                    // xs (32px): the chrome's compact control size.
                    size='xs'
                    variant='ghost'
                    {...chromeControlStyles}
                    _expanded={{
                        bg: '{colors.bg.active}',
                        color: '{colors.fg}',
                    }}
                >
                    {icon}
                </IconButton>
            </Popover.Trigger>
            <Portal>
                <Popover.Positioner>
                    <Popover.Content css={{ '--popover-size': 'sizes.3xs' }}>
                        <MyAccountPopoverContentBodyEl
                            rounded='md'
                            minH={0}
                            overflowY='auto'
                        >
                            <VStack align='stretch' gap={0} py={1.5} px={2}>
                                {typeof children === 'function'
                                    ? children(dismiss)
                                    : children}
                            </VStack>
                        </MyAccountPopoverContentBodyEl>
                    </Popover.Content>
                </Popover.Positioner>
            </Portal>
        </MyAccountMenuPopoverRootEl>
    );
}

IconMenuRoot.displayName = 'IconMenu';

/** Muted heading above a run of menu items — same treatment as the
 * sidebar's section labels (item-sized text, one gray step lighter). */
function IconMenuSectionTitle({ children }: { children: React.ReactNode }) {
    return (
        <Text color='{colors.fg.muted}' fontSize='xs' px={2} pt={1.5} pb={0.5}>
            {children}
        </Text>
    );
}

type IconMenuNamespace = typeof IconMenuRoot & {
    SectionTitle: typeof IconMenuSectionTitle;
};

const IconMenu = Object.assign(IconMenuRoot, {
    SectionTitle: IconMenuSectionTitle,
}) as IconMenuNamespace;

export { IconMenu, MyAccountMenu };
